import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { CardApi } from "../../api/CardApi";
import type { CardEditCardRequest } from "../../generated/dto/CardEditCardRequest";
import type { CardFetchOptions } from "../../generated/dto/CardFetchOptions";
import type { CardIssueFreeCardRequest } from "../../generated/dto/CardIssueFreeCardRequest";
import type { CardMember } from "../../generated/dto/CardMember";
import type { CardMemberFetchOptions } from "../../generated/dto/CardMemberFetchOptions";
import type { CardMemberSearchResult } from "../../generated/dto/CardMemberSearchResult";
import type { CardSearchParams } from "../../generated/dto/CardSearchParams";
import type { CardStaff } from "../../generated/dto/CardStaff";
import type { CardStaffSearchResult } from "../../generated/dto/CardStaffSearchResult";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class CardApiClient implements CardApi {
  constructor(private readonly client = useClient()) {}

  async getCardMember(id: number, options?: CardMemberFetchOptions): Promise<CardMember> {
    const { data } = await this.client.get<unknown>(`/card/member/${id}`, { params: serializeCbiDto(options, "generated.dto.CardMemberFetchOptions") });
    return deserializeCbiDto<CardMember>(data, "generated.dto.CardMember");
  }

  async getCardStaff(id: number, options?: CardFetchOptions): Promise<CardStaff> {
    const { data } = await this.client.get<unknown>(`/card/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CardFetchOptions") });
    return deserializeCbiDto<CardStaff>(data, "generated.dto.CardStaff");
  }

  async searchCardMember(params: CardSearchParams, page: number, perPage: number): Promise<PaginationResult<CardMemberSearchResult>> {
    const { data } = await this.client.post<unknown>(`/card/member/search`, serializeCbiDto(params, "generated.dto.CardSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CardMemberSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CardMemberSearchResult>>(result.items, "generated.dto.CardMemberSearchResult") };
    return result;
  }

  async searchCardStaff(params: CardSearchParams, page: number, perPage: number): Promise<PaginationResult<CardStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/card/staff/search`, serializeCbiDto(params, "generated.dto.CardSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CardStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CardStaffSearchResult>>(result.items, "generated.dto.CardStaffSearchResult") };
    return result;
  }

  async searchCardMemberFull(params: CardSearchParams, page: number, perPage: number, options?: CardMemberFetchOptions): Promise<PaginationResult<CardMember>> {
    const { data } = await this.client.post<unknown>(`/card/member/search/full`, serializeCbiDto(params, "generated.dto.CardSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CardMemberFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CardMember>;
    result = { ...result, items: deserializeCbiDto<Array<CardMember>>(result.items, "generated.dto.CardMember") };
    return result;
  }

  async searchCardStaffFull(params: CardSearchParams, page: number, perPage: number, options?: CardFetchOptions): Promise<PaginationResult<CardStaff>> {
    const { data } = await this.client.post<unknown>(`/card/staff/search/full`, serializeCbiDto(params, "generated.dto.CardSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CardFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CardStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CardStaff>>(result.items, "generated.dto.CardStaff") };
    return result;
  }

  async getCardStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff | null> {
    const { data } = await this.client.get<unknown>(`/card/staff/${id}/personid`, { params: serializeCbiDto(options, "generated.dto.PersonFetchOptions") });
    return deserializeCbiDto<PersonStaff | null>(data, "generated.dto.PersonStaff");
  }

  async editCardStaff(request: CardEditCardRequest): Promise<CardStaff> {
    const { data } = await this.client.post<unknown>(`/card/staff/editcard`, serializeCbiDto(request, "generated.dto.CardEditCardRequest"));
    let result = data as CardStaff;
    result = deserializeCbiDto<CardStaff>(result, "generated.dto.CardStaff");
    return result;
  }

  async issueFreeCardStaff(request: CardIssueFreeCardRequest): Promise<CardStaff> {
    const { data } = await this.client.post<unknown>(`/card/staff/issuefreecard`, serializeCbiDto(request, "generated.dto.CardIssueFreeCardRequest"));
    let result = data as CardStaff;
    result = deserializeCbiDto<CardStaff>(result, "generated.dto.CardStaff");
    return result;
  }
}

export const useCardApi = (): CardApi => new CardApiClient();
